import { Fragment, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@eureca/ui';
import { useDebouncedValue } from '@eureca/ui/forms';
import {
  ConsultantCopyableId,
  ConsultantFilterTrigger,
  ConsultantLayout,
  ConsultantListHeader,
  ConsultantListPlaceholder,
  ConsultantPageTitle,
  ConsultantSearchInput,
  ConsultantSortDropdown,
  ConsultantTablePagination,
  ConsultantTableSection,
  type ConsultantSortOption,
} from '@/shared/consultant-layout';
import { ClipboardListIcon } from '@/shared/consultants-menu/icons';
import { WorkspaceBrand } from '@/shared/workspace/WorkspaceBrand';
import {
  CheckIcon,
  LoaderCircleIcon,
  MoreHorizontalIcon,
  PlusIcon,
  TriangleAlertIcon,
} from '@eureca/ui/icons';
import { JobConfirmDialog } from '@/features/jobs/jobs-list/JobConfirmDialog';
import { companySearchQueryOptions } from '@/features/programs/program-create/company-search-query';
import { mePermissionsQueryOptions } from '@/shared/me-permissions-query';
import { formsListQueryOptions, type FormListEntry } from '../forms-query';
import { FormMutationError, useDeleteForm, useDuplicateForm } from '../forms-mutations';

// EU-454 + DEV-93: Form maintenance (create/edit/duplicate/delete) is gated on
// a Keycloak realm role — `gestor-conta` (Gestora de Conta) or `specialist`
// (especialista), with an `admin` bypass. The SPA reflects the backend decision:
// whoever lacks it gets a read-only list (actions hidden/disabled). The whole
// decision arrives as the single `canManageForms` boolean, so the copy names no
// role — DEV-93 dropped EU-454's "exclusive to Gestor de Conta" rule and the
// tooltip must not keep asserting it.
const MANAGE_FORMS_TOOLTIP = 'Você não tem permissão para criar e editar formulários.';

type FormsSortKey = 'name' | 'updatedAt' | 'createdAt';
type FormsSortDir = 'asc' | 'desc';

interface SortOption {
  key: FormsSortKey;
  dir: FormsSortDir;
  label: string;
}

// Mirrors the Programas sort menu shape — names alphabetical first, then
// chronological. `updatedAt desc` is treated as the implicit default so the
// trigger button isn't marked "active" out of the box.
const SORT_OPTIONS: SortOption[] = [
  { key: 'name', dir: 'asc', label: 'Nome (A → Z)' },
  { key: 'name', dir: 'desc', label: 'Nome (Z → A)' },
  { key: 'updatedAt', dir: 'desc', label: 'Mais recentes' },
  { key: 'updatedAt', dir: 'asc', label: 'Mais antigos' },
  { key: 'createdAt', dir: 'desc', label: 'Criados recentemente' },
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;

function formatBrDate(iso: string | Date): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return '';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = String(date.getFullYear()).slice(2);
  return `${dd}/${mm}/${yyyy}`;
}

export default function FormsListPage() {
  const navigate = useNavigate();
  // Workspace (área de trabalho) from the URL — the guard guarantees it. Seeds
  // the "Por empresa" filter and drives the header logo. The consultant can
  // still flip the in-page toggle to "Compartilhados" to see company-less forms.
  const { companyId: workspaceCompanyId } = useSearch({ from: '/_authenticated/formularios' });
  const [searchInput, setSearchInput] = useState('');
  const [companyFilter, setCompanyFilter] = useState<string>(workspaceCompanyId ?? '');
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [showShared, setShowShared] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<FormListEntry | null>(null);

  const [sort, setSort] = useState<FormsSortKey | undefined>(undefined);
  const [dir, setDir] = useState<FormsSortDir>('desc');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Debounce the raw input so a burst of keystrokes fires a single query;
  // TanStack Query cancels the prior in-flight fetch when `params` (the
  // queryKey) changes. The BFF rejects search shorter than 2 chars, so drop it
  // below the threshold. See `.claude/rules/server-side-search.md`.
  const debouncedSearchInput = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);
  const effectiveSearch =
    debouncedSearchInput.trim().length >= 2 ? debouncedSearchInput.trim() : undefined;

  // When "Mostrar compartilhados" is on, the company filter is logically
  // ignored (the SPA disables the picker and the BFF mirrors that). When
  // off, we forward the chosen company id (or omit when none).
  const params = useMemo(
    () => ({
      ...(effectiveSearch ? { search: effectiveSearch } : {}),
      ...(showShared ? { shared: true as const } : {}),
      ...(!showShared && companyFilter ? { companyId: companyFilter } : {}),
      page,
      pageSize: rowsPerPage,
      sort: sort ?? ('updatedAt' as const),
      dir,
    }),
    [effectiveSearch, showShared, companyFilter, page, rowsPerPage, sort, dir]
  );

  const formsQuery = useQuery(formsListQueryOptions(params));
  const companiesQuery = useQuery(companySearchQueryOptions(companySearchInput));
  const permissionsQuery = useQuery(mePermissionsQueryOptions());
  const deleteForm = useDeleteForm(pendingDelete?.id ?? '');
  const duplicateForm = useDuplicateForm();

  // Fail closed: while the projection is loading or errored (`data === undefined`)
  // the maintenance affordances stay hidden/disabled — the list is read-only
  // until the backend confirms the user holds one of the Form roles.
  const canManage = permissionsQuery.data?.canManageForms === true;
  // ...but "still deciding" is not the same as "denied". While the projection is
  // in flight the CTA is inert WITHOUT the denial tooltip, so a slow permission
  // response never flashes a permission message at someone who does have it.
  const isResolvingPermission = permissionsQuery.isPending;

  const companyOptions = (companiesQuery.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
  }));
  const selectedCompany = companyOptions.find((c) => c.id === companyFilter);

  function handleNew() {
    void navigate({ to: '/formularios/novo', search: { companyId: workspaceCompanyId } });
  }

  function handleEdit(formId: string) {
    void navigate({
      to: '/formularios/$formId/editar',
      params: { formId },
      search: { companyId: workspaceCompanyId },
    });
  }

  function handleAskDelete(form: FormListEntry) {
    setPendingDelete(form);
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    try {
      await deleteForm.mutateAsync();
      toast('Formulário excluído.', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      setPendingDelete(null);
    } catch (err) {
      const status = err instanceof FormMutationError ? err.status : 0;
      if (status === 404) {
        setPendingDelete(null);
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para excluir esse formulário.');
      } else {
        toast.error('Não foi possível excluir o formulário. Tente novamente.');
      }
    }
  }

  async function handleDuplicate(form: FormListEntry) {
    try {
      const cloned = await duplicateForm.mutateAsync({ id: form.id });
      toast('Formulário duplicado.', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      await navigate({
        to: '/formularios/$formId/editar',
        params: { formId: cloned.id },
        search: { companyId: workspaceCompanyId },
      });
    } catch (err) {
      const status = err instanceof FormMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Formulário não encontrado.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para duplicar esse formulário.');
      } else {
        toast.error('Não foi possível duplicar o formulário. Tente novamente.');
      }
    }
  }

  const items = formsQuery.data?.items ?? [];
  const total = formsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const isLoading = formsQuery.isLoading;
  const isEmpty = !isLoading && items.length === 0;

  const sortOptions: ConsultantSortOption[] = SORT_OPTIONS.map((option) => ({
    id: `${option.key}-${option.dir}`,
    label: option.label,
    isActive:
      sort === undefined
        ? option.key === 'updatedAt' && option.dir === 'desc'
        : option.key === sort && option.dir === dir,
  }));

  function handleSortSelect(id: string) {
    const opt = SORT_OPTIONS.find((o) => `${o.key}-${o.dir}` === id);
    if (!opt) return;
    setSort(opt.key);
    setDir(opt.dir);
    setPage(1);
  }

  const columns = (
    <TableHeader className="sticky top-0 z-10 border-b border-border bg-card">
      <TableRow>
        <TableHead resizable colKey="id" className="w-[80px]">
          ID
        </TableHead>
        <TableHead resizable colKey="name">
          Nome
        </TableHead>
        <TableHead resizable colKey="company">
          Empresa
        </TableHead>
        <TableHead resizable colKey="updated">
          Atualizado
        </TableHead>
        <TableHead aria-label="Ações" className="w-12" />
      </TableRow>
    </TableHeader>
  );

  return (
    <>
      <ConsultantLayout activeMenuId="forms">
        <ConsultantLayout.Header>
          <ConsultantListHeader
            title={
              <ConsultantPageTitle
                icon={<ClipboardListIcon className="size-4" />}
                brand={
                  workspaceCompanyId ? <WorkspaceBrand companyId={workspaceCompanyId} /> : undefined
                }
              >
                Formulários
              </ConsultantPageTitle>
            }
            actions={
              <CreateFormButton
                canManage={canManage}
                isResolving={isResolvingPermission}
                onClick={handleNew}
              />
            }
            filters={
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <ConsultantFilterTrigger
                      label={selectedCompany?.name ?? 'Empresa'}
                      count={!showShared && companyFilter ? 1 : 0}
                      disabled={showShared}
                      data-testid="forms-list-company-filter"
                    />
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Busque por empresas"
                        value={companySearchInput}
                        onValueChange={setCompanySearchInput}
                      />
                      <CommandList className="max-h-[300px] p-1">
                        <CommandEmpty>Nenhuma empresa encontrada</CommandEmpty>
                        {companyOptions.map((option) => {
                          const isSelected = option.id === companyFilter;
                          return (
                            <CommandItem
                              key={option.id}
                              value={option.name}
                              onSelect={() => setCompanyFilter(isSelected ? '' : option.id)}
                              data-active={isSelected || undefined}
                              className={cn(
                                'flex items-center justify-between gap-2 rounded-md px-2 py-1.5',
                                isSelected && 'bg-secondary/40'
                              )}
                            >
                              <span className="truncate">{option.name}</span>
                              {isSelected ? (
                                <span
                                  aria-hidden
                                  className="size-2 shrink-0 rounded-full bg-primary"
                                />
                              ) : null}
                            </CommandItem>
                          );
                        })}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <ConsultantFilterTrigger
                      label={showShared ? 'Compartilhados' : 'Por empresa'}
                      data-testid="forms-list-shared-toggle"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[200px] p-2">
                    <p className="px-2 pb-2 text-xs font-medium text-muted-foreground">Escopo</p>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup className="py-1">
                      <DropdownMenuItem
                        onClick={() => setShowShared(false)}
                        className="justify-between"
                      >
                        Por empresa
                        {!showShared ? <CheckIcon className="size-4 text-primary" /> : null}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setShowShared(true)}
                        className="justify-between"
                      >
                        Compartilhados
                        {showShared ? <CheckIcon className="size-4 text-primary" /> : null}
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>

                {companyFilter || showShared ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCompanyFilter('');
                      setCompanySearchInput('');
                      setShowShared(false);
                    }}
                  >
                    Limpar filtros
                  </Button>
                ) : null}
              </>
            }
            tools={
              <>
                <ConsultantSearchInput
                  value={searchInput}
                  onChange={setSearchInput}
                  ariaLabel="Buscar formulários"
                  placeholder="Buscar por nome..."
                  data-testid="forms-list-search"
                />
                <ConsultantSortDropdown
                  ariaLabel="Ordenar formulários"
                  options={sortOptions}
                  onSelect={handleSortSelect}
                  isActive={sort !== undefined}
                  data-testid="forms-list-sort"
                />
              </>
            }
          />
        </ConsultantLayout.Header>

        <ConsultantLayout.Body>
          {isLoading ? (
            <StatusPanel
              icon={<LoaderCircleIcon className="size-7 animate-spin" />}
              title="Buscando formulários..."
            />
          ) : isEmpty ? (
            <EmptyState onNew={handleNew} canManage={canManage} />
          ) : (
            <ConsultantTableSection
              ariaLabel="Lista de formulários"
              pagination={
                <ConsultantTablePagination
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  onRowsPerPageChange={(v) => {
                    setRowsPerPage(v);
                    setPage(1);
                  }}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  data-testid="forms-list-pagination"
                />
              }
            >
              <Table
                className="table-fixed"
                containerClassName="flex-1 overflow-auto"
                storageId="recruiter:forms-list"
              >
                {columns}
                <TableBody>
                  {items.map((form) => (
                    <FormRow
                      key={form.id}
                      form={form}
                      canManage={canManage}
                      onOpen={() => handleEdit(form.id)}
                      onDuplicate={() => void handleDuplicate(form)}
                      onDelete={() => handleAskDelete(form)}
                    />
                  ))}
                </TableBody>
              </Table>
            </ConsultantTableSection>
          )}
        </ConsultantLayout.Body>
      </ConsultantLayout>

      <JobConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteForm.isPending) setPendingDelete(null);
        }}
        title="Excluir formulário?"
        description={
          pendingDelete
            ? `Tem certeza que deseja excluir "${pendingDelete.name}"? Essa ação não pode ser desfeita.`
            : ''
        }
        confirmLabel={deleteForm.isPending ? 'Excluindo...' : 'Excluir'}
        onConfirm={() => void handleConfirmDelete()}
      />
    </>
  );
}

/**
 * "Criar formulário" CTA + tooltip wrapper for the Gestor de Conta gate
 * (EU-454). Radix Tooltip cannot trigger on a disabled button, so when disabled
 * we render a focusable <span> that hosts the tooltip target — mirrors the
 * company-create gate (`CompaniesPage.component.tsx`).
 */
function CreateFormButton({
  canManage,
  isResolving,
  onClick,
}: {
  canManage: boolean;
  isResolving: boolean;
  onClick: () => void;
}) {
  if (canManage) {
    return (
      <Button onClick={onClick} data-testid="forms-list-new">
        <PlusIcon />
        Criar formulário
      </Button>
    );
  }
  // Permission still in flight: inert, but NOT the denial state — no tooltip
  // wrapper, so nothing tells the user they lack a permission we haven't
  // finished checking.
  if (isResolving) {
    return (
      <Button disabled aria-busy data-testid="forms-list-new">
        <PlusIcon />
        Criar formulário
      </Button>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} data-testid="forms-list-new-disabled-wrapper">
            <Button disabled data-testid="forms-list-new">
              <PlusIcon />
              Criar formulário
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{MANAGE_FORMS_TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface FormRowProps {
  form: FormListEntry;
  canManage: boolean;
  onOpen: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

function FormRow({ form, canManage, onOpen, onDuplicate, onDelete }: FormRowProps) {
  return (
    <TableRow>
      <TableCell>
        <ConsultantCopyableId value={form.id} label="ID do formulário" />
      </TableCell>
      <TableCell>
        {/* EU-454: non-managers get a read-only row — the name is plain text
            (no editor entry point) and the actions kebab is hidden. */}
        {canManage ? (
          <button
            type="button"
            onClick={onOpen}
            className="rounded-sm text-left text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            {form.name}
          </button>
        ) : (
          <span className="text-sm font-medium text-foreground">{form.name}</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {form.companySlug ? (
          form.companySlug
        ) : (
          <Badge className="bg-secondary text-primary">Compartilhado</Badge>
        )}
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {formatBrDate(form.updatedAt)}
      </TableCell>
      <TableCell className="text-right">
        {canManage ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Ações para ${form.name}`}
                className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=open]:bg-border/50 data-[state=open]:text-foreground"
              >
                <MoreHorizontalIcon className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <Fragment>
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={onOpen}>Editar</DropdownMenuItem>
                  <DropdownMenuItem onClick={onDuplicate}>Duplicar</DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem destructive onClick={onDelete}>
                    Excluir
                  </DropdownMenuItem>
                </DropdownMenuGroup>
              </Fragment>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function StatusPanel({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <ConsultantListPlaceholder>
      <div className="flex size-16 items-center justify-center rounded-full bg-muted text-foreground">
        {icon}
      </div>
      <p className="text-2xl font-medium tracking-tight text-foreground">{title}</p>
    </ConsultantListPlaceholder>
  );
}

function EmptyState({ onNew, canManage }: { onNew: () => void; canManage: boolean }) {
  return (
    <ConsultantListPlaceholder>
      <div className="flex flex-col items-center gap-5">
        <div className="flex size-16 items-center justify-center rounded-full bg-muted text-foreground">
          <TriangleAlertIcon className="size-7" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <p className="text-2xl font-medium tracking-tight text-foreground">Sem formulários</p>
          <p className="text-base text-muted-foreground">
            {canManage
              ? 'No momento, ainda não há nenhum formulário cadastrado. Crie o primeiro para usar nas etapas do fluxo das vagas.'
              : 'No momento, ainda não há nenhum formulário cadastrado.'}
          </p>
        </div>
      </div>
      {/* EU-454 + DEV-93: creating needs a Form role — whoever lacks it sees a
          read-only empty state without the CTA. */}
      {canManage ? <Button onClick={onNew}>Criar formulário</Button> : null}
    </ConsultantListPlaceholder>
  );
}
